import { isNil, isEmpty, size, last, isArray, sortBy } from 'lodash';
import ServiceUrls from '@/lib/ServiceUrls';
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';
import $moment from '../lib/util/moment';
import HotelSettingsMixin from './HotelSettingsMixin';
import FilterMixin from './FilterMixin';

// define a mixin object
export default {

  mixins: [HotelSettingsMixin, FilterMixin],

  computed: {
    configurationErrors: function () {
      return this.getConfigurationErrors();
    },
  },

  methods: {
    getConfigurationErrors() {
      const ret = [];

      if (!isNil(this.hotelSettings)) {
        this.hotelSettings.ratePlans.forEach((it) => {
          ret.push(...this.__validateTaxes(it, 'RatePlan'));
        });
        this.hotelSettings.mealPlans.forEach((it) => {
          ret.push(...this.__validateTaxes(it, 'MealPlan'));
        });
        this.hotelSettings.inHouseServices.forEach((it) => {
          ret.push(...this.__validateTaxes(it, 'InHouseService'));
        });

        this.hotelSettings.taxes.forEach((it) => {
          if (!it.deleted && it.taxType === 'VAT' && isEmpty(it.vatLabel)) {
            ret.push({
              id: it.id,
              type: 'Tax',
              severity: 'Error',
              message: this.$t(
                'ConfigurationError.Tax.NoVATLabel',
                {
                  name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
                },
              ),
            });
          }
        });

        this.hotelSettings.taxes.forEach((it) => {
          if (!it.deleted && it.taxType === 'CityTax' && isEmpty(it.ageCategories)) {
            ret.push({
              id: it.id,
              type: 'Tax',
              severity: 'Warning',
              message: this.$t(
                'ConfigurationError.Tax.NoAgeCategory',
                {
                  name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
                },
              ),
            });
          }
        });

        if (!this.hotelSettings.taxes.some(it => !it.deleted && it.taxType === 'VAT' && it.amount === 0)) {
          ret.push({
            id: null,
            type: 'Tax',
            severity: 'Error',
            message: this.$t(
              'ConfigurationError.Tax.No0PctVAT',
            ),
          });
        }

        if (isEmpty(this.hotelSettings.details.emailReservation)) {
          ret.push({
            id: this.hotelSettings.hotel.id,
            type: 'Hotel',
            severity: 'Error',
            message: this.$t('ConfigurationError.Hotel.noReservationEmail'),
          });
        }

        if (isEmpty(this.hotelSettings.inHouseServices.filter(it => !it.deleted && it.type === 'PrePayment'))) {
          ret.push({
            id: this.hotelSettings.hotel.id,
            type: 'Hotel',
            severity: 'Error',
            message: this.$t('ConfigurationError.InHouseService.noPrepayment'),
          });
        }
      }

      return sortBy(ret, ['type', 'id', it => (it.severity === 'Error' ? 0 : 1)]);
    },
    __validateTaxes(it, type) {
      if (it.deleted) return [];

      const ret = [];
      if (isEmpty(it.applicableTaxes)) {
        // not tax at all
        ret.push({
          id: it.id,
          type: type,
          severity: 'Error',
          message: this.$t(
            `ConfigurationError.${type}.NoTax`,
            {
              name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
            },
          ),
        });
      } else if (isEmpty(it.applicableTaxes.filter(tax => tax.taxType === 'VAT'))) {
        // tax but no VAT
        ret.push({
          id: it.id,
          type: type,
          severity: 'Error',
          message: this.$t(
            `ConfigurationError.${type}.NoVAT`,
            {
              name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
            },
          ),
        });
      }

      if (type === 'RatePlan') {
        if (!isEmpty(this.hotelSettings.taxes.filter(tax => !tax.deleted && tax.taxType === 'CityTax'))
          && isEmpty(it.applicableTaxes.filter(tax => tax.taxType === 'CityTax'))) {
          // city tax exists but none defined for this rateplan
          ret.push({
            id: it.id,
            type: type,
            severity: 'Warning',
            message: this.$t(
              `ConfigurationError.${type}.NoCityTax`,
              {
                name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
              },
            ),
          });
        }
      }

      if (type === 'MealPlan') {
        if (isEmpty(it.ageCategories)) {
          ret.push({
            id: it.id,
            type: type,
            severity: 'Warning',
            message: this.$t(
              `ConfigurationError.${type}.NoAgeCategory`,
              {
                name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
              },
            ),
          });
        }
      }

      const vatDateRanges = sortBy(it.applicableTaxes.filter(tax => tax.taxType === 'VAT' && tax.isInclusiveTax).flatMap((tax) => {
        if (isEmpty(tax.validityDateRanges)) {
          return [{ startDate: '19700101', endDate: '20991231' }];
        } else {
          return tax.validityDateRanges.map(dateRange => ({ startDate: dateRange.startDate || '19700101', endDate: dateRange.endDate || '20991231' }));
        }
      }), 'startDate');

      if (vatDateRanges.length > 1) {
        for (let i = 1; i < vatDateRanges.length; i++) {
          // overlapping vat date range
          if (parseInt(vatDateRanges[i - 1].endDate, 10) > parseInt(vatDateRanges[i].startDate, 10)) {
            ret.push({
              id: it.id,
              type: type,
              severity: 'Error',
              message: this.$t(
                `ConfigurationError.${type}.OverlappingVAT`,
                {
                  name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
                  date1: this.$options.filters.toDate(vatDateRanges[i].startDate),
                  date2: this.$options.filters.toDate(vatDateRanges[i - 1].endDate),
                },
              ),
            });
          }
        }
      }

      if (!isEmpty(it.applicableTaxes)) {
        // no VAT set for date
        const today = $moment(new Date()).format('YYYYMMDD');
        for (let i = -7; i < (30 + 7); i++) {
          const date = parseInt($moment(today, 'YYYYMMDD').add(i, 'days').format('YYYYMMDD'), 10);

          if (isEmpty(vatDateRanges.find(dateRange => date >= parseInt(dateRange.startDate, 10) && date <= parseInt(dateRange.endDate, 10)))) {
            ret.push({
              id: it.id,
              type: type,
              severity: type === 'InHouseService' && it.type === 'PrePayment' ? 'Warning' : 'Error',
              message: this.$t(
                `ConfigurationError.${type}.NoVATForDate`,
                {
                  name: (it.name[this.$i18n.locale] || it.name[this.hotelSettings.details.primaryLanguage]),
                  date: this.$options.filters.toDate(`${date}`),
                },
              ),
            });
            break;
          }
        }
      }

      return ret;
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/ConfigErrorMixin.js